import { Controller } from "@hotwired/stimulus"

// Modale "info movimento" sul registro giornaliero (solo movimenti con id persistito).
// L'apertura usa capture su document (prima del listener capture sul form di register-day-validation)
// così non compete con Stimulus bubble né con stopImmediatePropagation sulla Verifica.
export default class extends Controller {
  static targets = ["overlay", "content"]

  connect() {
    const el = document.getElementById("register-day-movement-info-labels")
    if (el) {
      try {
        this.labelMap = JSON.parse(el.textContent)
      } catch {
        this.labelMap = {}
      }
    } else {
      this.labelMap = {}
    }

    this.boundDocumentCapture = this.handleDocumentCapture.bind(this)
    document.addEventListener("click", this.boundDocumentCapture, true)
  }

  disconnect() {
    if (this.boundDocumentCapture) {
      document.removeEventListener("click", this.boundDocumentCapture, true)
    }
  }

  handleDocumentCapture(event) {
    const opener = event.target.closest("[data-register-day-movement-info-open]")
    if (!opener || opener.disabled) return
    if (!this.element.contains(opener)) return

    event.preventDefault()
    event.stopImmediatePropagation()

    const id = opener.dataset.movementId
    if (!id) return
    if (!this.hasOverlayTarget || !this.hasContentTarget) return

    void this.loadDetails(id)
  }

  async loadDetails(id) {
    const L = this.labelMap || {}
    this.contentTarget.innerHTML = `<p class="text-sm text-gray-500">${this.escapeHtml(L.loading || "…")}</p>`
    this.overlayTarget.classList.remove("hidden")

    try {
      const res = await fetch(`/movements/${encodeURIComponent(id)}/register_day_row_details.json`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      this.contentTarget.innerHTML = this.buildHtml(data, L)
    } catch (e) {
      this.contentTarget.innerHTML = `<p class="text-sm text-red-600">${this.escapeHtml(e.message)}</p>`
    }
  }

  close(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.overlayTarget.classList.add("hidden")
  }

  overlayClick(event) {
    if (event.target === this.overlayTarget) this.close(event)
  }

  stopPropagation(event) {
    if (event) event.stopPropagation()
  }

  buildHtml(data, L = this.labelMap || {}) {
    const row = (label, val) => {
      const v = val === null || val === undefined || val === "" ? "—" : String(val)
      return `<div class="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-x-4 gap-y-1 border-b border-gray-100 py-2 last:border-b-0">
        <dt class="text-gray-500 font-medium">${this.escapeHtml(label)}</dt>
        <dd class="text-gray-900 font-mono text-xs break-all">${this.escapeHtml(v)}</dd>
      </div>`
    }

    const afterWithDelta = (afterVal, beforeVal) => {
      if (afterVal === null || afterVal === undefined || afterVal === "") return "—"
      const a = Number(afterVal)
      const b = Number(beforeVal)
      if (Number.isNaN(a) || beforeVal === null || beforeVal === undefined || beforeVal === "" || Number.isNaN(b)) {
        return String(afterVal)
      }
      const d = a - b
      const sign = d > 0 ? "+" : ""
      return `${afterVal} (${sign}${d.toFixed(2)})`
    }

    const parts = [
      row(L.id || "ID", data.id),
      row(L.timestamp || "Timestamp", data.timestamp),
      row(L.zone_from || "Zona origine", data.zone_from),
      row(L.zone_to || "Zona destinazione", data.zone_to),
      row(L.quantity || "Quantità", data.quantity),
      row(L.warning || "Avvisi", data.warning),
      row(L.inventory_origin_before || "Giacenza origine (prima)", data.inventory_origin_before),
      row(L.inventory_origin_after || "Giacenza origine (dopo)", afterWithDelta(data.inventory_origin_after, data.inventory_origin_before)),
      row(L.inventory_destination_before || "Giacenza destinazione (prima)", data.inventory_destination_before),
      row(L.inventory_destination_after || "Giacenza destinazione (dopo)", afterWithDelta(data.inventory_destination_after, data.inventory_destination_before)),
    ]

    return `<div class="space-y-0">${parts.join("")}</div>`
  }

  escapeHtml(str) {
    if (str === null || str === undefined) return ""
    const s = String(str)
    return s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
